import { escapeHtml } from '../utils/html';
import type { WatchdogRow, WatchdogStatus } from './watchdog-types';
import { WATCHDOG_STATUS_META } from './watchdog-types';
import {
  watchdogRows,
  wdSortCol, wdSortAsc, wdSearch, wdStatusFilter, wdGroupFilter, wdModelFilter,
  wdPage, wdPageSize, wdLastUpdated,
  setWdSortCol, setWdSortAsc, setWdStatusFilter, setWdPage,
  formatDuration, formatLastComm, wdRecalculate,
} from './watchdog-page';

// "Not Active" (never communicated) is a distinct exception category, not a severity level, so it
// stays last — same placement as Geotab's own Watchdog Report legend.
const STATUS_ORDER: WatchdogStatus[] = ['ok', 'offline1', 'offline2', 'offline3', 'notActive'];

// The buckets shown as comparison bars in the overview card. OK is deliberately excluded — at a
// typical fleet it's ~90%+ of devices, so sharing one scale with it would flatten these four into
// near-invisible slivers. Scaling them against each other instead makes the actionable differences
// (e.g. 81 vs 12) legible.
const ATTENTION_ORDER: WatchdogStatus[] = ['offline1', 'offline2', 'offline3', 'notActive'];

interface BucketCounts {
  ok: number; offline1: number; offline2: number; offline3: number; notActive: number;
  total: number; totalOffline: number;
}

function bucketCounts(rows: WatchdogRow[]): BucketCounts {
  const c: BucketCounts = {
    ok: 0, offline1: 0, offline2: 0, offline3: 0, notActive: 0,
    total: rows.length, totalOffline: 0,
  };
  for (const r of rows) c[r.status]++;
  c.totalOffline = c.offline1 + c.offline2 + c.offline3;
  return c;
}

// ── State machine (empty / loading / error / results) ─────────────────────────

export function showWatchdogState(s: 'empty' | 'loading' | 'error' | 'results', msg = ''): void {
  document.getElementById('wdEmpty')!  .classList.toggle('hidden', s !== 'empty');
  document.getElementById('wdLoading')!.classList.toggle('hidden', s !== 'loading');
  document.getElementById('wdErrBox')! .classList.toggle('hidden', s !== 'error');
  document.getElementById('wdResults')!.classList.toggle('hidden', s !== 'results');

  if (s === 'loading') {
    const lbl = document.getElementById('wdLoadingLabel');
    if (lbl) lbl.textContent = msg || 'Loading…';
  }
  if (s === 'error') {
    document.getElementById('wdErrBox')!.textContent = msg;
    document.getElementById('wdEmpty')!.classList.remove('hidden');
  }
}

// ── Main render entry ─────────────────────────────────────────────────────────

export function renderWatchdogResults(): void {
  // Any re-render (including a silent 30s background poll) replaces the table the row menu was
  // anchored to — hide it first so it can't be left floating over stale, now-detached coordinates.
  hideRowMenu();

  const counts = bucketCounts(watchdogRows);
  renderOverview(counts);
  renderKpis(counts);
  populateFilterOptions();
  renderTable();
  updateLastUpdatedLabel();

  const w = window as Window & {
    __wdSortBy?: (c: string) => void;
    __wdTroubleshoot?: (id: string) => void;
    __wdRowMenu?: (ev: MouseEvent, id: string) => void;
    __wdCopySerial?: (id: string) => void;
    __wdFilterStatus?: (status: string) => void;
  };
  w.__wdSortBy = (col: string) => {
    if (wdSortCol === col) setWdSortAsc(!wdSortAsc);
    else { setWdSortCol(col); setWdSortAsc(true); }
    setWdPage(0);
    wdRecalculate();
  };
  w.__wdTroubleshoot = (id: string) => {
    hideRowMenu();
    const row = watchdogRows.find((r) => r.id === id);
    if (row) openTroubleshootModal(row);
  };
  w.__wdRowMenu = (ev: MouseEvent, id: string) => { ev.stopPropagation(); openRowMenu(ev, id); };
  w.__wdCopySerial = (id: string) => {
    hideRowMenu();
    const row = watchdogRows.find((r) => r.id === id);
    if (row) void navigator.clipboard?.writeText(row.serialNumber).catch(() => { /* clipboard blocked */ });
  };
  // Clicking a KPI card or an overview bar jumps straight to that status in the table below,
  // tying the fleet-wide summary to the drill-down view instead of leaving them display-only.
  w.__wdFilterStatus = (status: string) => {
    setWdStatusFilter(status as WatchdogStatus);
    setWdPage(0);
    const sel = document.getElementById('wdStatusFilter') as HTMLSelectElement | null;
    if (sel) sel.value = status;
    wdRecalculate();
    document.getElementById('wdTableSection')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
}

// ── Overview donut + legend ────────────────────────────────────────────────────

function renderOverview(c: BucketCounts): void {
  const card = document.getElementById('wdOverviewCard');
  if (!card) return;

  const needsAttention = c.total - c.ok;
  const okPct   = c.total > 0 ? Math.round((c.ok / c.total) * 100) : 0;
  const maxAttn = Math.max(c.offline1, c.offline2, c.offline3, c.notActive, 1);

  // Devices that are bucketed offline by their last-logged timestamp but whose live
  // isDeviceCommunicating flag has already flipped back to true — actively reconnecting right
  // now, ahead of a full status report catching up. Surfacing this is the whole point of
  // capturing the flag: don't make a manager troubleshoot a device that's already coming back.
  const reconnecting = watchdogRows.filter((r) => r.status !== 'ok' && r.isCommunicating).length;

  const attnClass = needsAttention > 0 ? 'wd-hero-attn-warn' : 'wd-hero-attn-clear';
  const attnText  = needsAttention > 0
    ? `${needsAttention.toLocaleString()} device${needsAttention === 1 ? '' : 's'} need attention` +
      (reconnecting > 0 ? ` &middot; ${reconnecting} reconnecting` : '')
    : 'All devices communicating normally';

  const bars = ATTENTION_ORDER.map((k) => {
    const val = c[k];
    // Minimum visible width for any non-zero bucket — otherwise a small-but-real count (e.g. 1
    // device against a max of 300) would round down to an invisible sliver.
    const widthPct = val === 0 ? 0 : Math.max(4, Math.round((val / maxAttn) * 100));
    const label = WATCHDOG_STATUS_META[k].label;
    return `
      <div class="wd-bar-row" role="button" tabindex="0" aria-label="Filter table to ${escapeHtml(label)}"
           onclick="window.__wdFilterStatus('${k}')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();window.__wdFilterStatus('${k}')}">
        <span class="wd-dot wd-dot-${k}"></span>
        <span class="wd-bar-label">${escapeHtml(label)}</span>
        <span class="wd-bar-track"><span class="wd-bar-fill wd-bar-fill-${k}" style="width:${widthPct}%"></span></span>
        <span class="wd-bar-val">${val.toLocaleString()}</span>
      </div>`;
  }).join('');

  card.innerHTML = `
    <div class="wd-overview-title">Device Status Overview</div>
    <div class="wd-overview-hero">
      <div class="wd-hero-total">
        <div class="wd-hero-total-val">${c.total.toLocaleString()}</div>
        <div class="wd-hero-total-lbl">Total Devices</div>
      </div>
      <div class="wd-hero-divider"></div>
      <div class="wd-hero-health">
        <div class="wd-hero-ok">
          <span class="wd-dot wd-dot-ok"></span>${c.ok.toLocaleString()} OK
          <span class="wd-hero-ok-pct">&middot; ${okPct}% of fleet</span>
        </div>
        <div class="${attnClass}">${attnText}</div>
      </div>
    </div>
    <div class="wd-bars">${bars}</div>`;
}

// ── KPI cards (with static decorative sparklines) ──────────────────────────────

// Fixed decorative curve — same shape on every card, tinted per bucket. Conveys no data.
const SPARK_VALUES = [7, 9, 8, 12, 10, 13, 11, 15, 12, 16, 14, 12, 15, 13, 17, 15];

function buildSparkline(colorVar: string): string {
  const W = 100, H = 30;
  const max = Math.max(...SPARK_VALUES), min = Math.min(...SPARK_VALUES);
  const pts = SPARK_VALUES.map((v, i) => {
    const x = (i / (SPARK_VALUES.length - 1)) * W;
    const y = H - 2 - ((v - min) / (max - min)) * (H - 6);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const area = `M0,${H} L${pts.join(' L')} L${W},${H} Z`;
  return `
    <svg class="wd-spark" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true">
      <path class="wd-spark-area" d="${area}" fill="${colorVar}" />
      <polyline class="wd-spark-line" points="${pts.join(' ')}" fill="none" stroke="${colorVar}" />
    </svg>`;
}

const CLOCK_ICON = `<svg viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" stroke-width="1.5"/><polyline points="9,5 9,9 12,11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const CHECK_ICON = `<svg viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" stroke-width="1.5"/><polyline points="6,9.5 8,11.5 12,7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const OFF_ICON   = `<svg viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" stroke-width="1.5"/><line x1="4.6" y1="4.6" x2="13.4" y2="13.4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;

function renderKpis(c: BucketCounts): void {
  const grid = document.getElementById('wdKpiGrid');
  if (!grid) return;

  const pct = (v: number) => (c.total > 0 ? Math.round((v / c.total) * 100) : 0);
  const cards = STATUS_ORDER.map((k) => {
    const meta = WATCHDOG_STATUS_META[k];
    const val  = c[k];
    const icon = k === 'ok' ? CHECK_ICON : k === 'notActive' ? OFF_ICON : CLOCK_ICON;
    return `
      <div class="wd-kpi wd-kpi-${k} wd-kpi-clickable" role="button" tabindex="0"
           aria-label="Filter table to ${escapeHtml(meta.label)}"
           onclick="window.__wdFilterStatus('${k}')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();window.__wdFilterStatus('${k}')}">
        <div class="wd-kpi-head">
          <span class="wd-kpi-label">${escapeHtml(meta.label.toUpperCase())}</span>
          <span class="wd-kpi-icon">${icon}</span>
        </div>
        <div class="wd-kpi-val">${val.toLocaleString()}</div>
        <div class="wd-kpi-sub">${pct(val)}% of total</div>
        ${buildSparkline(meta.colorVar)}
      </div>`;
  }).join('');

  grid.innerHTML = cards;
}

// ── Filter dropdown options (group + model) ───────────────────────────────────

function populateFilterOptions(): void {
  fillSelect('wdGroupFilter', distinct(watchdogRows.map((r) => r.group)), wdGroupFilter, 'All Groups');
  fillSelect('wdModelFilter', distinct(watchdogRows.map((r) => r.model)), wdModelFilter, 'All Models');
}

function distinct(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

function fillSelect(id: string, options: string[], current: string, allLabel: string): void {
  const sel = document.getElementById(id) as HTMLSelectElement | null;
  if (!sel) return;
  const opts = [`<option value="all">${allLabel}</option>`]
    .concat(options.map((o) => `<option value="${escapeHtml(o)}">${escapeHtml(o)}</option>`))
    .join('');
  sel.innerHTML = opts;
  sel.value = options.includes(current) ? current : 'all';
}

// ── Table ──────────────────────────────────────────────────────────────────────

const SORT_VALUE: Record<string, (r: WatchdogRow) => string | number> = {
  name:         (r) => r.name.toLowerCase(),
  serialNumber: (r) => r.serialNumber.toLowerCase(),
  group:        (r) => r.group.toLowerCase(),
  status:       (r) => STATUS_ORDER.indexOf(r.status),
  lastComm:     (r) => (r.offlineMs === null ? Infinity : r.offlineMs),
  duration:     (r) => (r.offlineMs === null ? Infinity : r.offlineMs),
};

function renderTable(): void {
  const cols: { key: string; label: string }[] = [
    { key: 'name',         label: 'Device Name' },
    { key: 'serialNumber', label: 'Serial Number' },
    { key: 'group',        label: 'Group' },
    { key: 'status',       label: 'Status' },
    { key: 'lastComm',     label: 'Last Communication' },
    { key: 'duration',     label: 'Duration' },
  ];

  // 1 — filter (status / group / model / search)
  const term = wdSearch.toLowerCase().trim();
  const filtered = watchdogRows.filter((r) => {
    if (wdStatusFilter !== 'all' && r.status !== wdStatusFilter) return false;
    if (wdGroupFilter  !== 'all' && r.group  !== wdGroupFilter)  return false;
    if (wdModelFilter  !== 'all' && r.model  !== wdModelFilter)  return false;
    if (term && !r.name.toLowerCase().includes(term) && !r.serialNumber.toLowerCase().includes(term)) return false;
    return true;
  });

  // 2 — sort
  const valOf = SORT_VALUE[wdSortCol] ?? SORT_VALUE.name;
  const dir = wdSortAsc ? 1 : -1;
  const sorted = [...filtered].sort((a, b) => {
    const av = valOf(a), bv = valOf(b);
    if (av < bv) return -dir;
    if (av > bv) return dir;
    return 0;
  });

  // 3 — paginate
  const pageSize    = wdPageSize > 0 ? wdPageSize : sorted.length || 1;
  const totalPages  = Math.ceil(sorted.length / pageSize) || 1;
  const currentPage = Math.min(wdPage, totalPages - 1);
  const pageStart   = currentPage * pageSize;
  const pageRows    = sorted.slice(pageStart, pageStart + pageSize);

  // 4 — header
  const thHtml = cols.map((col) => {
    const active = col.key === wdSortCol;
    const arrow  = active ? (wdSortAsc ? '▲' : '▼') : '▲';
    return `<th class="${active ? 'sort-active' : ''}" onclick="window.__wdSortBy('${col.key}')">${col.label}<span class="sort-arrow ${active ? '' : 'sort-arrow-dim'}">${arrow}</span></th>`;
  }).join('') + '<th class="wd-th-menu"></th>';

  // 5 — rows
  const rowsHtml = pageRows.map((r) => {
    const meta       = WATCHDOG_STATUS_META[r.status];
    const isOffline  = r.status !== 'ok';
    const rowAttrs   = isOffline
      ? ` class="wd-row wd-row-offline" onclick="window.__wdTroubleshoot('${r.id}')" title="View troubleshooting steps"`
      : ' class="wd-row"';
    return `<tr${rowAttrs}>
      <td><span class="wd-dot wd-dot-${r.status}"></span><span class="wd-name">${escapeHtml(r.name)}</span></td>
      <td class="td-mono">${escapeHtml(r.serialNumber)}</td>
      <td>${escapeHtml(r.group)}</td>
      <td>
        <span class="wd-pill wd-pill-${r.status}">${escapeHtml(meta.label)}</span>
        ${isOffline && r.isCommunicating ? '<span class="wd-reconnect-badge"><span class="wd-reconnect-dot"></span>Reconnecting</span>' : ''}
      </td>
      <td class="td-mono">${escapeHtml(formatLastComm(r.lastComm))}</td>
      <td class="td-mono">${escapeHtml(formatDuration(r.offlineMs))}</td>
      <td class="wd-td-menu"><button class="wd-menu-btn" title="Actions" onclick="window.__wdRowMenu(event,'${r.id}')">⋮</button></td>
    </tr>`;
  }).join('');

  const table = document.getElementById('wdTable') as HTMLElement | null;
  if (table) table.innerHTML = `<thead><tr>${thHtml}</tr></thead><tbody>${rowsHtml}</tbody>`;

  // 6 — count + pagination
  const countEl = document.getElementById('wdCount');
  if (countEl) countEl.textContent = `${filtered.length.toLocaleString()} device${filtered.length === 1 ? '' : 's'}`;
  renderPagination(currentPage, totalPages, sorted.length, pageStart, pageSize, pageRows.length);
}

function renderPagination(
  currentPage: number, totalPages: number, totalRows: number,
  pageStart: number, pageSize: number, shown: number,
): void {
  const el = document.getElementById('wdPagination');
  if (!el) return;

  const visible = new Set<number>([0, totalPages - 1]);
  for (let i = Math.max(0, currentPage - 2); i <= Math.min(totalPages - 1, currentPage + 2); i++) visible.add(i);

  const btns: string[] = [];
  let prev = -1;
  for (const p of [...visible].sort((a, b) => a - b)) {
    if (p > prev + 1) btns.push('<span class="la-pg-ellipsis">…</span>');
    btns.push(`<button class="la-pg-btn${p === currentPage ? ' la-pg-active' : ''}" data-page="${p}">${p + 1}</button>`);
    prev = p;
  }

  const pageEnd = totalRows === 0 ? 0 : pageStart + shown;
  const nav = totalPages <= 1 ? '' : `
    <button class="la-pg-btn" data-page="${currentPage - 1}" ${currentPage === 0 ? 'disabled' : ''}>‹</button>
    ${btns.join('')}
    <button class="la-pg-btn" data-page="${currentPage + 1}" ${currentPage >= totalPages - 1 ? 'disabled' : ''}>›</button>`;

  el.innerHTML = `${nav}<span class="la-pg-info">Showing ${totalRows === 0 ? 0 : pageStart + 1} to ${pageEnd} of ${totalRows.toLocaleString()} devices</span>`;
}

// ── Row action menu (⋮) ────────────────────────────────────────────────────────

function openRowMenu(ev: MouseEvent, id: string): void {
  const menu = document.getElementById('wdRowMenu');
  const row = watchdogRows.find((r) => r.id === id);
  if (!menu || !row) return;

  const items: string[] = [];
  if (row.status !== 'ok') {
    items.push(`<button class="wd-menu-item" onclick="window.__wdTroubleshoot('${id}')">Troubleshoot device</button>`);
  }
  items.push(`<button class="wd-menu-item" onclick="window.__wdCopySerial('${id}')">Copy serial number</button>`);
  menu.innerHTML = items.join('');
  menu.classList.remove('hidden');   // must be visible before measuring offsetWidth/Height below

  const btn  = ev.currentTarget as HTMLElement;
  const rect = btn.getBoundingClientRect();
  const mw = menu.offsetWidth  || 180;
  const mh = menu.offsetHeight || 80;

  // Flip the menu above the button when it wouldn't fit below — otherwise the ⋮ menu on one of
  // the last visible rows renders partly or fully below the viewport and is unreachable.
  const fitsBelow = rect.bottom + 4 + mh <= window.innerHeight;
  menu.style.top  = fitsBelow ? `${rect.bottom + 4}px` : `${Math.max(8, rect.top - mh - 4)}px`;
  menu.style.left = `${Math.max(8, rect.right - mw)}px`;
}

export function hideRowMenu(): void {
  document.getElementById('wdRowMenu')?.classList.add('hidden');
}

// ── Troubleshoot modal (offline rows only) ─────────────────────────────────────

const TROUBLESHOOT_STEPS: string[] = [
  'Confirm the device is plugged in correctly — there should be no gap between the OBD port and the Geotab device.',
  'If a harness is used, make sure it is not unplugged or damaged, and that the Geotab device is zip-tied securely to the harness.',
  'With the vehicle off, disconnect the Geotab device for about 30 seconds, then plug it back in and start the vehicle.',
  'The device light will turn on and the device will beep while it regains connection.',
  'Once the lights are on and the beeping has stopped (approximately 3 minutes), drive the vehicle for 10 to 15 minutes.',
  'Afterward, check the database to confirm whether the vehicle is back Online.',
];

export function openTroubleshootModal(row: WatchdogRow): void {
  const modal = document.getElementById('wdModal');
  const body  = document.getElementById('wdModalBody');
  if (!modal || !body) return;

  const meta = WATCHDOG_STATUS_META[row.status];
  const steps = TROUBLESHOOT_STEPS.map((s) => `<li>${escapeHtml(s)}</li>`).join('');

  // The device's live signal is already back, ahead of its logged data catching up — flag this
  // prominently so a manager doesn't spend time troubleshooting a device that's already recovering.
  const reconnectNote = row.isCommunicating ? `
    <div class="wd-ts-reconnect-note">
      <span class="wd-reconnect-dot"></span>
      <strong>This device appears to be reconnecting.</strong> It is currently transmitting a live
      signal, even though its logged data hasn't caught up yet — that can take a few minutes.
      Consider waiting before working through the steps below.
    </div>` : '';

  body.innerHTML = `
    <div class="wd-ts-device">
      <div class="wd-ts-name"><span class="wd-dot wd-dot-${row.status}"></span>${escapeHtml(row.name)}</div>
      <span class="wd-pill wd-pill-${row.status}">${escapeHtml(meta.label)}</span>
    </div>
    <div class="wd-ts-meta">
      <div><span class="wd-ts-meta-lbl">Serial</span><span class="td-mono">${escapeHtml(row.serialNumber)}</span></div>
      <div><span class="wd-ts-meta-lbl">Group</span><span>${escapeHtml(row.group)}</span></div>
      <div><span class="wd-ts-meta-lbl">Last communication</span><span class="td-mono">${escapeHtml(formatLastComm(row.lastComm))}</span></div>
      <div><span class="wd-ts-meta-lbl">Offline for</span><span class="td-mono">${escapeHtml(formatDuration(row.offlineMs))}</span></div>
    </div>
    ${reconnectNote}
    <div class="wd-ts-steps-title">Troubleshooting steps</div>
    <ol class="wd-ts-steps">${steps}</ol>
    <div class="wd-ts-support">
      If the issue persists, email our support team at
      <a href="mailto:support@et-envue.com">support@et-envue.com</a>.
    </div>`;

  modal.classList.add('show');
}

// ── "Last updated" stamp ───────────────────────────────────────────────────────

export function updateLastUpdatedLabel(): void {
  const el = document.getElementById('wdLastUpdated');
  if (!el) return;
  if (!wdLastUpdated) { el.textContent = 'Last updated: —'; return; }
  const secs = Math.floor((Date.now() - wdLastUpdated.getTime()) / 1000);
  const rel = secs < 5 ? 'Just now'
            : secs < 60 ? `${secs}s ago`
            : secs < 3600 ? `${Math.floor(secs / 60)}m ago`
            : `${Math.floor(secs / 3600)}h ago`;
  el.textContent = `Last updated: ${rel}`;
}
